import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCarrierZipPrefixes, deleteCarrierZipPrefix, patchCarrierZipPrefix, getCarriers, logError } from '../api';
import { Form, Row, Col } from 'react-bootstrap';
import GenericList from './Global/GenericList';
import DisabledToggle from './ui/DisabledToggle';
import { useDisabledToggle } from '../hooks/useDisabledToggle';
import { useSorting } from '../hooks/useSorting';
import useFilterPersistence from '../hooks/useFilterPersistence';

const CarrierZipPrefixList = () => {
    const [carrierZipPrefixes, setCarrierZipPrefixes] = useState([]);
    const [filteredPrefixes, setFilteredPrefixes] = useState([]);
    const [carriers, setCarriers] = useState([]);
    const [selectedCarrier, setSelectedCarrier] = useFilterPersistence('filters_carrier_zip_prefixes', '');
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useFilterPersistence('pagination_carrier_zip_prefixes', { page: 1, pageSize: 50 });
    const currentPage = pagination.page;
    const itemsPerPage = pagination.pageSize;
    const { includeDisabled, setIncludeDisabled } = useDisabledToggle();
    const { orderBy, orderDesc, handleSort } = useSorting();
    const navigate = useNavigate();

    const loadCarrierZipPrefixes = useCallback(async () => {
        try {
            const response = await getCarrierZipPrefixes(includeDisabled, currentPage, itemsPerPage, orderBy, orderDesc);
            setCarrierZipPrefixes(response.items || response);
            setTotalCount(response.totalCount || response.length);
        } catch (error) {
            logError(error);
            console.error('Error loading carrier zip prefixes:', error);
        }
    }, [includeDisabled, currentPage, itemsPerPage, orderBy, orderDesc]);

    const loadCarriers = useCallback(async () => {
        try {
            const data = await getCarriers(true);
            setCarriers(data);
        } catch (error) {
            logError(error);
        }
    }, []);

    useEffect(() => {
        const filtered = selectedCarrier 
            ? carrierZipPrefixes.filter(item => item.carrierId === parseInt(selectedCarrier))
            : carrierZipPrefixes;
        setFilteredPrefixes(filtered);
    }, [carrierZipPrefixes, selectedCarrier]);

    useEffect(() => {
        loadCarrierZipPrefixes();
        loadCarriers();
    }, [loadCarrierZipPrefixes, loadCarriers]);

    const handleDelete = async (item) => {
        try {
            await deleteCarrierZipPrefix(item.id);
            const updated = carrierZipPrefixes.filter(i => i.id !== item.id);
            setCarrierZipPrefixes(updated);
        } catch (error) {
            logError(error);
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            await patchCarrierZipPrefix(id, { id, disabled: !currentStatus });
            const updated = carrierZipPrefixes.map((item) => 
                item.id === id 
                    ? { ...item, disabled: !currentStatus }
                    : item
            );
            setCarrierZipPrefixes(updated);
        } catch (error) {
            logError(error);
        }
    };

    const handleEdit = (item) => {
        navigate(`/carrier-zip-prefixes/edit/${item.id}`);
    };

    const columns = [
        { key: 'id', header: 'Id', sortable: true },
        { 
            key: 'carrier', 
            header: 'Transportista', 
            render: (item) => item.carrier ? `${item.carrier.firstName} ${item.carrier.lastName1}` : 'Sin asignar'
        },
        { key: 'zipPrefix', header: 'Prefijo', sortable: true },
        { key: 'priority', header: 'Prioridad', sortable: true },
        { key: 'notes', header: 'Notas' }
    ];

    return (
        <div>
            <Row className="mb-4">
                <Col xs={12} md={6}>
                    <h1>Prefijos de Código Postal por Transportista</h1>
                </Col>
                <Col xs={12} md={6} className="d-flex justify-content-end align-items-center">
                    <DisabledToggle 
                        includeDisabled={includeDisabled}
                        onChange={setIncludeDisabled}
                    />
                </Col>
            </Row>

            <Row className="mb-3">
                <Col xs={12} md={4}>
                    <Form.Select 
                        value={selectedCarrier} 
                        onChange={(e) => setSelectedCarrier(e.target.value)}
                        data-testid="carrier-filter-select"
                    >
                        <option value="">Todos los transportistas</option>
                        {carriers.map(carrier => (
                            <option key={carrier.id} value={carrier.id}>
                                {carrier.firstName} {carrier.lastName1}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            <GenericList
                title=""
                data={filteredPrefixes}
                columns={columns}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onToggleStatus={handleToggleStatus}
                createRoute="/carrier-zip-prefixes/new"
                createButtonText="Nuevo Prefijo"
                deleteMessage="¿Estás seguro de que deseas eliminar este prefijo?"
                containerFluid={false}
                totalCount={totalCount}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setPagination({ ...pagination, page })}
                onItemsPerPageChange={(pageSize) => setPagination({ page: 1, pageSize })}
                sortable={true}
                onSort={handleSort}
            />
        </div>
    );
};

export default CarrierZipPrefixList;